import { useEffect, useState } from "react";
import { useSiteContent } from "@/context/SiteContentContext";
import { Check } from "lucide-react";

// Lek input that commits on blur/Enter (avoids a save per keystroke)
function LekInput({ value, onCommit, testId }) {
    const [v, setV] = useState(value ?? "");
    useEffect(() => { setV(value ?? ""); }, [value]);
    const commit = () => {
        const n = Math.max(0, parseFloat(v) || 0);
        if (n !== (parseFloat(value) || 0)) onCommit(n);
    };
    return (
        <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            <input type="number" min="0" step="50" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-20 border border-slate-300 px-1.5 py-0.5 text-right font-mono text-[11px] bg-white" data-testid={testId} />
            <span className="font-mono text-[10px] text-slate-500">Lek</span>
        </span>
    );
}

export default function FailureReasonsPicker({ value = [], onChange, testIdPrefix = "failure-reason", kind = "reasons", prices, onPricesChange }) {
    const { content } = useSiteContent();
    const isServices = kind === "services";
    const options = (isServices ? content.invoice_services : content.invoice_failure_reasons) || [];
    const title = isServices ? content.invoice_service_note_title || "Shërbimet që duhen bërë te makina" : content.invoice_reasons_title || "Arsyet e prishjes";
    const defaults = content.invoice_service_prices || {};
    const withPrices = isServices && !!onPricesChange;
    // keep items saved on old sales even if removed from the master list
    const all = [...options, ...value.filter((v) => !options.includes(v))];
    if (all.length === 0) return null;

    const priceOf = (r) => (prices && r in prices ? prices[r] : defaults[r] || 0);
    const toggle = (r) => {
        const on = value.includes(r);
        onChange(on ? value.filter((x) => x !== r) : [...value, r]);
    };

    return (
        <div data-testid={`${testIdPrefix}-picker`}>
            <label className="label-kicker mb-1 block">{title}</label>
            <div className="grid sm:grid-cols-2 gap-1.5">
                {all.map((r, i) => {
                    const on = value.includes(r);
                    return (
                        <div
                            key={r}
                            role="button"
                            tabIndex={0}
                            onClick={() => toggle(r)}
                            onKeyDown={(e) => (e.key === " " || e.key === "Enter") && e.target === e.currentTarget && (e.preventDefault(), toggle(r))}
                            className={`flex items-center gap-2 text-left text-xs px-2 py-1.5 border cursor-pointer transition-colors ${on ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 text-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                            data-testid={`${testIdPrefix}-${i}`}
                            aria-pressed={on}
                        >
                            <span className={`w-4 h-4 shrink-0 border flex items-center justify-center ${on ? "bg-[hsl(var(--primary))] border-[hsl(var(--primary))]" : "border-slate-400 bg-white"}`}>
                                {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                            </span>
                            <span className="flex-1">{r}</span>
                            {withPrices && on && (
                                <LekInput value={priceOf(r)} onCommit={(n) => onPricesChange({ ...(prices || {}), [r]: n })} testId={`${testIdPrefix}-price-${i}`} />
                            )}
                            {withPrices && !on && defaults[r] > 0 && (
                                <span className="font-mono text-[10px] text-slate-400">{Number(defaults[r]).toLocaleString()} Lek</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
